/**
 * The block tiles, uploaded to the GPU as a texture array.
 *
 * The pixels themselves come from `@lofi/shared` so the game and the preview
 * tool run the same generator — see `packages/shared/src/textures.ts` for what
 * is drawn and why.
 *
 * ## ⚠️ Why a texture array and not an atlas
 *
 * One 16×16 layer per tile, rather than tiles packed side by side in one image.
 * **An atlas bleeds:** mipmapping averages neighbouring pixels, so at distance
 * a tile picks up colour from whatever happened to be packed next to it, and
 * `mosahraun` beside `sandur` would fringe green into black. A texture array
 * has no neighbours to bleed from, and three 0.180 can sample a layer per
 * vertex through `TextureNode.depth()` — verified in the installed build, not
 * assumed.
 *
 * ## ⚠️ It DOES get mipmaps. T94 said it did not, and that was wrong.
 *
 * The handoff of 2026-08-07 recorded, as the cause of distant ground crawling,
 * that `DataArrayTexture` never receives a mip chain and that three's upload
 * path "loops mip levels for `isDataTexture` but only layers for
 * `isDataArrayTexture`". **Read in the installed r180 source, that is not what
 * happens**, on either backend:
 *
 * - **WebGPU** — `Textures.updateTexture` computes `levels` from
 *   `needsMipmaps`, creates the texture with `mipLevelCount: 5`, and then calls
 *   `WebGPUTextureUtils.generateMipmaps`, which loops `texture.image.depth` and
 *   builds a full chain **per layer** (`three.webgpu.js` ~67543).
 * - **WebGL2** — `gl.generateMipmap( TEXTURE_2D_ARRAY )` (~62439).
 *
 * The two three.js issues cited in support are both **closed and fixed**:
 * [#18134](https://github.com/mrdoob/three.js/issues/18134) added array-texture
 * mipmaps to WebGL (PR #18165), and
 * [#31143](https://github.com/mrdoob/three.js/issues/31143) was a
 * `FramebufferTexture` repeat bug fixed in **r177** — we run r180.
 *
 * ⚠️ **And the experiment that "confirmed" it could not have.** Flipping the
 * crisp/smooth switch changes `magFilter` and anisotropy only; `minFilter` is
 * `LinearMipmapLinearFilter` in **both** positions, three lines below. Nothing
 * about that switch has ever touched minification, so its result was never
 * evidence about mipmaps either way.
 *
 * ## ⚠️ Where the colour lives now
 *
 * The **tile** carries the block's colour; the **vertex** carries only ambient
 * occlusion × value jitter, as a grey. That split is what makes the fringe
 * possible at all — moss spilling over the top of the lava beneath it is a
 * colour change *inside* one face, and no per-vertex colour can express it.
 */

import { DataArrayTexture, LinearMipmapLinearFilter, NearestFilter, SRGBColorSpace } from 'three';
import { DEFAULT_TILE, renderTile, tilePlans, type FaceKind } from '@lofi/shared';

export interface BlockTextures {
  readonly texture: DataArrayTexture;
  /** Which layer a given block face samples. `0` for anything unknown. */
  layerOf(block: number, face: FaceKind): number;
}

const layerKey = (block: number, face: FaceKind): string => `${block}:${face}`;

/**
 * Build every tile the palette needs, once, at startup.
 *
 * 29 blocks × 3 faces at 16×16 RGBA is under 90 KB — small enough that
 * generating it beats shipping and decoding a PNG, and it means there is no
 * binary asset in git that can silently drift from the data describing it.
 */
export function buildBlockTextures(tile: number = DEFAULT_TILE): BlockTextures {
  const plans = tilePlans();
  const bytes = tile * tile * 4;
  const data = new Uint8Array(bytes * plans.length);
  const index = new Map<string, number>();

  /* `entries()` rather than an index, so nothing reads an array by a variable
     — which is what `security/detect-object-injection` flags — and so the
     "what if it is undefined" branch that only existed for the compiler goes
     away with it. */
  for (const [i, plan] of plans.entries()) {
    index.set(layerKey(plan.block, plan.face), i);
    data.set(renderTile(plan, tile), i * bytes);
  }

  const texture = new DataArrayTexture(data, tile, tile, plans.length);
  texture.colorSpace = SRGBColorSpace;
  /* ⚠️ Nearest when magnified, mipmapped when minified. Nearest up close keeps
     a 16×16 tile crisp rather than blurred — the look haugur's eye is
     calibrated to, and the reason he could identify granite from a thumbnail.
     Mipmaps into the distance stop far ground shimmering as the camera moves. */
  texture.magFilter = NearestFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  return {
    texture,
    layerOf: (block, face) => index.get(layerKey(block, face)) ?? 0,
  };
}
